import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { AlertCircle, MailCheck, RefreshCw } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Logo } from '@/components/brand/Logo';
import { Button } from '@/components/ui/Button';
import { Field, Input } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';

/**
 * Layar setelah pendaftaran: menunggu konfirmasi email.
 *
 * Pendaftaran sengaja tidak memberi sesi, jadi halaman ini adalah tempat
 * pengguna berhenti sampai ia membuka tautan di emailnya. Tugasnya cuma dua:
 * menjelaskan apa yang harus dilakukan, dan menyediakan cara mengirim ulang
 * kalau emailnya tidak kunjung datang.
 *
 * Pengiriman ulang menuntut kata sandi karena pengguna belum punya sesi.
 * Tanpa itu, siapa pun bisa membanjiri alamat orang lain dengan email
 * konfirmasi hanya dengan mengetik alamatnya.
 */
export const PeriksaEmailPage = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const toast = useToast();

  const [email, setEmail] = useState(state?.email ?? '');
  const [sandi, setSandi] = useState('');
  const [bukaForm, setBukaForm] = useState(false);

  const gagalKirim = state?.terkirim === false;

  const kirimUlang = useMutation({
    mutationFn: async () => {
      // Masuk sebentar untuk mendapat token, lalu minta kirim ulang. Server
      // menolak sesi bagi akun yang belum terverifikasi, jadi jalur ini
      // memang satu-satunya yang tersedia sebelum konfirmasi.
      await api.post('/auth/login', { email, password: sandi }).catch((err) => {
        // 403 dengan alasan belum terverifikasi justru yang kita harapkan:
        // artinya kata sandinya benar. Yang lain berarti kredensial salah.
        if (err instanceof ApiError && err.details?.alasan === 'email_belum_terverifikasi') return;
        throw err;
      });
      return api.post('/auth/verify-email/request');
    },
    onSuccess: () => {
      toast.success('Tautan dikirim ulang', {
        description: `Periksa kotak masuk ${email}, termasuk folder spam.`,
      });
      setBukaForm(false);
      setSandi('');
    },
    onError: (err) =>
      toast.error('Gagal mengirim ulang', {
        description:
          err instanceof ApiError
            ? err.message
            : 'Periksa email dan kata sandi Anda, lalu coba lagi.',
      }),
  });

  return (
    <div className="grid min-h-screen place-items-center bg-[var(--surface-canvas)] px-4">
      <div className="w-full max-w-md text-center">
        <Link to="/" className="mb-8 inline-flex">
          <Logo className="h-8 w-auto" />
        </Link>

        <div className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-raised)] p-8 shadow-sm">
          <div
            className={
              gagalKirim
                ? 'mx-auto grid size-14 place-items-center rounded-full bg-warning-50 dark:bg-warning-500/10'
                : 'mx-auto grid size-14 place-items-center rounded-full bg-accent-50 dark:bg-accent-500/10'
            }
          >
            {gagalKirim ? (
              <AlertCircle className="size-7 text-warning-600" aria-hidden="true" />
            ) : (
              <MailCheck className="size-7 text-accent-600" aria-hidden="true" />
            )}
          </div>

          <h1 className="mt-4 text-xl font-bold text-[var(--text-primary)]">
            {gagalKirim ? 'Akun dibuat, email belum terkirim' : 'Periksa email Anda'}
          </h1>

          <p className="mt-3 text-sm leading-relaxed text-[var(--text-secondary)]">
            {state?.pesan ??
              `Kami mengirim tautan konfirmasi ke ${email || 'alamat email Anda'}. Buka tautannya untuk mengaktifkan akun.`}
          </p>

          <p className="mt-3 text-sm text-[var(--text-muted)]">
            Belum masuk juga setelah beberapa menit? Periksa folder spam atau promosi.
          </p>

          {!bukaForm ? (
            <div className="mt-6 space-y-2">
              <Button
                variant="secondary"
                icon={RefreshCw}
                className="w-full"
                onClick={() => setBukaForm(true)}
              >
                Kirim ulang tautannya
              </Button>
              <Button variant="ghost" className="w-full" onClick={() => navigate('/masuk')}>
                Sudah dikonfirmasi, masuk sekarang
              </Button>
            </div>
          ) : (
            <form
              className="mt-6 space-y-3 text-left"
              onSubmit={(e) => {
                e.preventDefault();
                kirimUlang.mutate();
              }}
            >
              <Field label="Email">
                {(props) => (
                  <Input
                    {...props}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                  />
                )}
              </Field>
              <Field label="Kata sandi" hint="Diperlukan supaya orang lain tidak bisa membanjiri alamat Anda.">
                {(props) => (
                  <Input
                    {...props}
                    type="password"
                    value={sandi}
                    onChange={(e) => setSandi(e.target.value)}
                    autoComplete="current-password"
                  />
                )}
              </Field>
              <div className="flex gap-2 pt-1">
                <Button variant="ghost" type="button" onClick={() => setBukaForm(false)}>
                  Batal
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  loading={kirimUlang.isPending}
                  disabled={!email || !sandi}
                >
                  Kirim ulang
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
